/**
 * Form Layout Helper
 * Renders a responsive CSS grid form from field definitions.
 *
 * @typedef {Object} FieldDef
 * @property {string} name
 * @property {string} label
 * @property {string} type        - 'text' | 'date' | 'time' | 'select' | 'textarea' | 'number'
 * @property {boolean} [fullWidth] - Force full row span
 * @property {Array} [options]     - For select type [{value, label}]
 * @property {string} [placeholder]
 * @property {boolean} [required]
 * @property {Object} [attrs]       - Extra HTML attributes merged onto the input element
 */

/**
 * Render the appropriate input element HTML for a field definition.
 * @param {FieldDef} field
 * @returns {string} HTML string for the input element
 */
function renderInput(field) {
  const name = escapeAttr(field.name);
  const placeholder = field.placeholder ? ` placeholder="${escapeAttr(field.placeholder)}"` : '';
  const required = field.required ? ' required' : '';
  const extraAttrs = field.attrs
    ? Object.entries(field.attrs)
        .map(([k, v]) => ` ${escapeAttr(k)}="${escapeAttr(String(v))}"`)
        .join('')
    : '';

  switch (field.type) {
    case 'textarea':
      return `<textarea id="${name}" name="${name}"${placeholder}${required}${extraAttrs} rows="3"></textarea>`;

    case 'select': {
      const opts = (field.options || [])
        .map((o) => `<option value="${escapeAttr(o.value)}">${escapeHtml(o.label)}</option>`)
        .join('');
      return `<select id="${name}" name="${name}"${required}${extraAttrs}><option value="">Select…</option>${opts}</select>`;
    }

    case 'checkbox':
      return `<input type="checkbox" id="${name}" name="${name}"${extraAttrs} />`;

    default:
      return `<input type="${escapeAttr(field.type)}" id="${name}" name="${name}"${placeholder}${required}${extraAttrs} />`;
  }
}

/**
 * Escape a string for use in an HTML attribute value.
 * @param {string} str
 * @returns {string}
 */
function escapeAttr(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Escape a string for use in HTML text content.
 * @param {string} str
 * @returns {string}
 */
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * renderFormGrid(fields: FieldDef[]) → string (HTML)
 *
 * Returns a CSS grid container. Fields with fullWidth or type=textarea
 * span all columns via the `form-grid__field--full` class.
 * Others get the `form-grid__field` class and fill columns left-to-right.
 *
 * Each field wrapper contains a <label>, the input element, and a
 * <span class="field-error"> placeholder for validation messages.
 */
export function renderFormGrid(fields) {
  const fieldHtml = fields
    .map((field) => {
      const isFullWidth = field.fullWidth === true || field.type === 'textarea';
      const wrapperClass = isFullWidth ? 'form-grid__field--full' : 'form-grid__field';
      const name = escapeAttr(field.name);
      const label = escapeHtml(field.label);
      const requiredMark = field.required ? ' <span class="field-required">*</span>' : '';

      return `<div class="${wrapperClass}">
  <label for="${name}">${label}${requiredMark}</label>
  ${renderInput(field)}
  <span class="field-error" data-field="${name}"></span>
</div>`;
    })
    .join('\n');

  return `<div class="form-grid">\n${fieldHtml}\n</div>`;
}
